import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Boxes, Plus, X, Trash2, Activity, ChevronLeft, ChevronRight,
  Coins, Wallet, Package, FlameKindling, Eye, EyeOff, TrendingUp, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { RobuxIcon, WalletValue, useAuth, walletAmount } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';
import { DynamicCasePreview, DEFAULT_SHOWCASE } from '../components/CasePreview';
import { ShowcaseEditor } from '../components/ShowcaseEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Backend ile birebir aynı ekonomi sabitleri
const PAYOUT_RATIO = 0.94;           // %6 house edge → price = EV / 0.94
const HOUSE_EDGE = 0.06;
const CREATOR_RATE = 0.10;           // creator house edge'in %10'u = price'ın %0.6'sı
const MAX_CASES = 10;
const MIN_ITEMS = 2;
const MIN_PRICE = 100;   // ⚠️ SİTE birimi (katalog kanonik kaynağı)

// Oluşturma ekranında kullanılan sabit site-birimi biçimlendirici.
// Aktif cüzdana göre DEĞİŞMEZ: burada seçilen fiyatlar katalog fiyatıdır,
// oyun sırasında gösterilecek BB fiyatı ondan türetilir.
const fmtSitePrice = (v) => walletAmount(v, 'main');

// Kısaltmalı format (1.37K, milyon+ → 1,000,000) — CaseBattles ile aynı
const formatCompact = (number) => {
  const raw = typeof number === 'string' ? parseFloat(number.replace(/,/g, '')) : Number(number);
  if (!raw || isNaN(raw)) return "0";
  const sign = raw < 0 ? "-" : "";
  const abs = Math.abs(raw);
  const trim = (v) => v.toFixed(2).replace(/\.?0+$/, "");
  if (abs >= 1000000) return sign + abs.toLocaleString();
  if (abs >= 1e3) return sign + trim(abs / 1e3) + "K";
  return sign + trim(abs);
};

// ==========================================
// DYNAMIC CASE PREVIEW (CaseBattles/AdminCases ile aynı görsel dil)
// ==========================================
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı: ALTI dosyada altı kopya zamanla
// ayrışıyor ve her düzeltme altı yere dokunmayı gerektiriyordu.
// Kasa görseli: color: ise DynamicCasePreview, değilse <img>
const CaseVisual = ({ caseObj, className = "" }) => {
  const isColor = caseObj.image_url?.startsWith('color:');
  if (isColor) {
    return (
      <div className={`relative flex items-center justify-center ${className}`}>
        <div className="scale-[0.5]">
          <DynamicCasePreview caseColor={caseObj.image_url} items={caseObj.items || []} showcase={caseObj.showcase} />
        </div>
      </div>
    );
  }
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <img src={caseObj.image_url} alt={caseObj.name} className="w-16 h-16 object-contain drop-shadow-md" />
    </div>
  );
};

// ==========================================
// KASA KARTI (kullanıcının kendi case'i)
// ==========================================
const MyCaseCard = React.memo(({ caseObj, onDelete }) => {
  // 🍀 Kasa ve item fiyatları AKTİF cüzdanın biriminde gelir; biçimlendirme
  // tek noktadan (fmtMoney) geçer ki mili-LC ham basılmasın.
  // ⚠️ `wallet` DE ALINMALI: kart içindeki <WalletValue> tutarları aktif
  // cüzdanın biriminde gösteriyor. Alınmadığında bileşen render sırasında
  // `ReferenceError: wallet is not defined` atıp TÜM sayfayı düşürüyordu.
  const { fmtMoney, wallet } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const active = caseObj.is_active;
  const items = caseObj.items || [];
  const sortedItems = [...items].sort((a, b) => (b.price || 0) - (a.price || 0));

  // Onay 3 saniye sonra otomatik sıfırlanır
  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 3000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.25 }}
      className={`rounded-2xl border overflow-hidden shadow-lg ${
        active ? 'bg-[#1A1D20] border-[#2A2D31] hover:border-[#B059E8]/40' : 'bg-[#1A1D20]/50 border-[#2A2D31]/50'
      }`}
    >
      {/* ÜST: görsel + isim + fiyat + durum */}
      <div className="p-4 flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-xl bg-[#101214] border border-[#2A2D31]/70 flex items-center justify-center shrink-0">
          <CaseVisual caseObj={caseObj} className="w-full h-full" />
          {!active && (
            <span className="absolute -top-1.5 -right-1.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300">
              Inactive
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-black text-white truncate">{caseObj.name}</h3>
            {active
              ? <span className="w-2 h-2 rounded-full bg-[#2ecf6e] shadow-[0_0_8px_#2ecf6e]" />
              : <span className="w-2 h-2 rounded-full bg-zinc-600" />}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-lg font-black text-white mm-num">
            {/* Backend kasa fiyatlarını aktif cüzdanın biriminde gönderiyor
                (decorate_cases_for_wallet); EV testini geçemeyen kasalar
                LC listesinden zaten düşürülüyor. */}
            <WalletValue amount={caseObj.price} wallet={wallet} />
          </div>
          <p className="text-[11px] text-zinc-600 mt-0.5 font-medium">{items.length} items</p>
        </div>

        {/* İstatistikler: açılma + kazanç */}
        <div className="hidden sm:flex flex-col gap-2 shrink-0">
          <div className="flex items-center gap-2 bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-1.5">
            <FlameKindling className="w-4 h-4 text-[#c98bf0]" />
            <div className="leading-tight">
              <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider">Opened</p>
              <p className="text-sm font-black text-white mm-num">{formatCompact(caseObj.total_opens || 0)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-1.5">
            <TrendingUp className="w-4 h-4 text-[#2ecf6e]" />
            <div className="leading-tight">
              <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider">Earned</p>
              <p className="text-sm font-black text-[#2ecf6e] mm-num">{/* 🔒 D3 — komisyon havuzu DAİMA site parası cinsindendir. */}
              {formatCompact(caseObj.total_earned || 0)}<RobuxIcon size={13} className="ml-1" /></p>
            </div>
          </div>
        </div>

        {/* Aksiyonlar */}
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => setExpanded(e => !e)}
            className="px-3 py-2 rounded-lg bg-[#2A2D31]/50 hover:bg-[#33363b] text-zinc-300 text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 transition-colors"
          >
            {expanded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {expanded ? 'Hide' : 'Contents'}
          </button>
          {active && (
            <button
              onClick={() => {
                if (confirmDelete) { onDelete(caseObj); setConfirmDelete(false); }
                else setConfirmDelete(true);
              }}
              className={`px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 transition-colors ${
                confirmDelete
                  ? 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500'
                  : 'bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border-rose-500/20'
              }`}
            >
              <Trash2 className="w-3.5 h-3.5" /> {confirmDelete ? 'Sure?' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {/* Mobil istatistik şeridi */}
      <div className="sm:hidden flex items-center gap-3 px-4 pb-3">
        <div className="flex items-center gap-1.5 text-xs">
          <FlameKindling className="w-3.5 h-3.5 text-[#c98bf0]" />
          <span className="text-zinc-400 font-bold">{formatCompact(caseObj.total_opens || 0)} opens</span>
        </div>
        <span className="text-[#2A2D31]">|</span>
        <div className="flex items-center gap-1.5 text-xs">
          <TrendingUp className="w-3.5 h-3.5 text-[#2ecf6e]" />
          <span className="text-[#2ecf6e] font-bold">{/* 🔒 D3 — komisyon havuzu DAİMA site parası cinsindendir. */}
              {formatCompact(caseObj.total_earned || 0)}<RobuxIcon size={13} className="ml-1" /> earned</span>
        </div>
      </div>

      {/* KASA İÇERİĞİ (genişletilebilir) */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#2A2D31] bg-[#101214]/60 overflow-hidden"
          >
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {sortedItems.map((item, i) => (
                <div
                  key={i}
                  className="bg-[#15171a] border border-[#2A2D31] rounded-lg p-2 flex flex-col items-center gap-1"
                  style={{ borderColor: `${item.color || '#2A2D31'}44` }}
                >
                  <img src={item.image_url} alt={item.name} className="w-10 h-10 object-contain drop-shadow-md" />
                  <p className="text-[10px] font-bold truncate w-full text-center" style={{ color: item.color || '#fff' }}>{item.name}</p>
                  <p className="text-[10px] text-[#2ecf6e] mm-num font-bold"><WalletValue amount={item.price} wallet={wallet} /></p>
                  <p className="text-[9px] text-zinc-500 font-bold">%{item.chance}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

// ==========================================
// CREATE CASE MODAL (AdminCases mantığı, community endpoint)
// ==========================================
const CreateCaseModal = ({ token, onClose, onCreated }) => {
  const [pool, setPool] = useState([]);
  const [loadingPool, setLoadingPool] = useState(true);
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#B059E8');
  const [selected, setSelected] = useState([]); // [{item_id,name,price,color,image_url,weight}]
  // 🎨 Vitrin yerleşimi — SADE mod: sürükle + boyut.
  const [showcase, setShowcase] = useState(DEFAULT_SHOWCASE);
  const [activeSlot, setActiveSlot] = useState(0);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/game/cases/community/items`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPool(res.data || []);
      } catch {
        toast.error("Failed to load item pool.");
      } finally {
        setLoadingPool(false);
      }
    })();
  }, [token]);

  const toggleItem = (item) => {
    const exists = selected.find(i => i.item_id === item.id);
    if (exists) setSelected(selected.filter(i => i.item_id !== item.id));
    else setSelected([...selected, { item_id: item.id, name: item.name, price: item.price, color: item.color, image_url: item.image_url, weight: 10 }]);
  };

  const updateWeight = (item_id, val) => {
    const w = Math.max(0, parseInt(val) || 0);
    setSelected(selected.map(i => i.item_id === item_id ? { ...i, weight: w } : i));
  };

  // Backend ile birebir aynı: EV/0.94
  const { price, totalWeight } = useMemo(() => {
    const tw = selected.reduce((s, i) => s + (i.weight || 0), 0);
    if (tw === 0) return { price: 0, totalWeight: 0 };
    let ev = 0;
    selected.forEach(i => { ev += i.price * (i.weight / tw); });
    return { price: Math.floor(ev / PAYOUT_RATIO), totalWeight: tw };
  }, [selected]);

  const previewItems = selected.map(i => ({ image_url: i.image_url, weight: i.weight }));

  const filteredPool = pool.filter(i => i.name?.toLowerCase().includes(search.toLowerCase()));

  const handlePublish = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 3 || trimmed.length > 30) return toast.error("Case name must be 3-30 characters!");
    if (selected.length < MIN_ITEMS) return toast.error(`Add at least ${MIN_ITEMS} items!`);
    if (totalWeight <= 0) return toast.error("Total weight must be positive!");
    // ⚠️ Karşılaştırma SİTE biriminde: hem `price` hem MIN_PRICE katalog birimi.
    if (price < MIN_PRICE)
      return toast.error(`Calculated price (${fmtSitePrice(price)}) is below the minimum `
                       + `(${fmtSitePrice(MIN_PRICE)}). Add more valuable items.`);

    const payload = {
      name: trimmed,
      image_url: `color:${color}`,
      items: selected.map(i => ({ item_id: i.item_id, chance_weight: i.weight })),
      // 🎨 Sunucu sınırları ayrıca uyguluyor — istemciye güvenilmiyor.
      showcase: showcase,
    };

    setPublishing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/game/cases/community/create`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Community case published!");
      // Yeni kasa için düzeni sıfırla.
      setShowcase(DEFAULT_SHOWCASE);
      setActiveSlot(0);
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create case!");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl max-h-[90vh] bg-[#15171a] border border-[#2A2D31] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2D31] shrink-0">
          <h2 className="text-xl font-black text-white uppercase tracking-wider flex items-center gap-2.5">
            <Package className="w-6 h-6 text-[#B059E8]" /> Build Your Case
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden">
          {/* SOL: item havuzu */}
          <div className="lg:col-span-2 border-r border-[#2A2D31] flex flex-col min-h-0">
            <div className="p-4 border-b border-[#2A2D31] shrink-0">
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search items..."
                className="w-full bg-[#101214] border border-[#2A2D31] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#B059E8]"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin">
              {loadingPool ? (
                <div className="py-12 flex justify-center"><Activity className="w-6 h-6 text-[#B059E8] animate-spin" /></div>
              ) : filteredPool.length === 0 ? (
                <p className="text-center text-zinc-600 text-sm py-8">No items found.</p>
              ) : filteredPool.map(item => {
                const isSel = selected.find(i => i.item_id === item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item)}
                    className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer transition-all ${
                      isSel ? 'bg-[#B059E8]/10 border-[#B059E8]' : 'bg-[#101214] border-[#2A2D31] hover:border-[#3a3d43]'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={item.image_url} alt={item.name} className="w-9 h-9 object-contain drop-shadow-md shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: item.color }}>{item.name}</p>
                        {/* ⚠️ Katalog fiyatı — DAİMA site birimi (bkz. fmtSitePrice). */}
                        <p className="text-xs text-[#2ecf6e] mm-num">{fmtSitePrice(item.price)}</p>
                      </div>
                    </div>
                    {isSel && <div className="w-4 h-4 rounded-full bg-[#B059E8] shadow-[0_0_10px_rgba(176,89,232,0.8)] shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SAĞ: tasarım + seçili itemlar + fiyat */}
          <div className="lg:col-span-3 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
              {/* İsim + renk + preview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="text-[11px] text-zinc-400 font-bold uppercase tracking-wide">Case Name</label>
                    <input
                      value={name} onChange={e => setName(e.target.value)} maxLength={30}
                      placeholder="e.g. Lucky Box"
                      className="w-full bg-[#101214] border border-[#2A2D31] rounded-xl px-4 py-3 text-white mt-1.5 outline-none focus:border-[#B059E8]"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1">{name.trim().length}/30 — min 3 characters</p>
                  </div>
                  <div>
                    <label className="text-[11px] text-zinc-400 font-bold uppercase tracking-wide">Case Color</label>
                    <div className="flex items-center gap-3 mt-1.5">
                      <input type="color" value={color} onChange={e => setColor(e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer bg-[#101214] border-2 border-[#2A2D31]" />
                      <span className="text-sm mm-num font-bold text-zinc-300 bg-[#101214] px-3 py-2 rounded-lg border border-[#2A2D31]">
                        {color.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                {/* 🎨 Sade vitrin editörü: sürükleyerek konumlandır,
                    tek kaydırıcıyla boyutlandır. Açı ve hassas X/Y
                    yalnızca yönetici panelinde. */}
                <ShowcaseEditor
                  simple
                  caseColor={`color:${color}`}
                  items={previewItems}
                  showcase={showcase}
                  setShowcase={setShowcase}
                  activeSlot={activeSlot}
                  setActiveSlot={setActiveSlot}
                />
              </div>

              {/* Seçili itemlar + weight */}
              <div className="bg-[#101214] border border-[#2A2D31] rounded-xl p-4">
                <h3 className="text-xs font-bold text-zinc-300 uppercase tracking-wide mb-3">
                  Selected Items & Odds <span className="text-zinc-600">({selected.length})</span>
                </h3>
                {selected.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-6">Pick at least {MIN_ITEMS} items from the pool.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.map((item) => {
                      const pct = totalWeight > 0 ? ((item.weight / totalWeight) * 100).toFixed(2) : '0.00';
                      return (
                        <div key={item.item_id} className="flex items-center justify-between bg-[#1A1D20] p-2.5 rounded-lg border border-white/5 gap-2">
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <img src={item.image_url} alt={item.name} className="w-8 h-8 object-contain drop-shadow-md shrink-0" />
                            <span className="text-sm font-bold truncate" style={{ color: item.color }}>{item.name}</span>
                          </div>
                          <span className="text-[#2ecf6e] mm-num text-xs font-bold shrink-0 hidden sm:block">{fmtSitePrice(item.price)}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <input
                              type="number" min="1" value={item.weight}
                              onChange={e => updateWeight(item.item_id, e.target.value)}
                              className="w-16 bg-[#101214] border border-[#2A2D31] rounded px-2 py-1 text-white text-sm outline-none text-center focus:border-[#B059E8]"
                            />
                            <span className="text-xs font-bold text-[#c98bf0] min-w-[44px] text-right">%{pct}</span>
                            <button onClick={() => toggleItem({ id: item.item_id })} className="text-zinc-600 hover:text-rose-400 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Alt bar: fiyat + publish */}
            <div className="border-t border-[#2A2D31] p-5 flex items-center justify-between gap-4 shrink-0 bg-[#15171a]">
              <div>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wide">Calculated Price</p>
                <div className="flex items-end gap-1.5">
                  <span className="text-3xl font-black text-white mm-num">{price.toLocaleString()}</span>
                  <span className="text-lg font-bold text-[#2ecf6e] mb-0.5">$</span>
                </div>
                <p className="text-[10px] text-zinc-600 mt-0.5">
                  %6 house edge — you earn %0.6 per open (≈{formatCompact(Math.floor(price * HOUSE_EDGE * CREATOR_RATE))}<RobuxIcon size={13} className="ml-1" />)
                </p>
              </div>
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="px-7 py-3.5 text-white font-black uppercase tracking-widest rounded-xl transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 shadow-[0_0_25px_rgba(176,89,232,0.4)] flex items-center gap-2"
                style={{ backgroundColor: '#B059E8', backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 9px, transparent 9px, transparent 20px)' }}
              >
                {publishing ? <Activity className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5" /> Publish</>}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==========================================
// ANA SAYFA
// ==========================================
export const CommunityCases = () => {
  const { token, isAuthenticated, fmtMoney, wallet } = useAuth();
  const [data, setData] = useState({ cases: [], active_count: 0, remaining_slots: MAX_CASES, total_earnings: 0, claimable_vault: 0 });
  const [loading, setLoading] = useState(true);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [showCreate, setShowCreate] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const fetchMine = async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/game/cases/community/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      if (error.response?.status !== 401) toast.error("Failed to load your cases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMine(); /* eslint-disable-next-line */ }, [token]);

  const handleClaim = async () => {
    if ((data.claimable_vault || 0) <= 0) return;
    setClaiming(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/game/cases/community/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Claimed ${formatCompact(res.data.claimed_amount)}$!`);
      fetchMine();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nothing to claim.");
    } finally {
      setClaiming(false);
    }
  };

  const handleDelete = async (caseObj) => {
    try {
      await axios.post(`${BACKEND_URL}/api/game/cases/community/delete`, { case_id: caseObj.id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Case deleted.");
      fetchMine();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete case.");
    }
  };

  const activeCount = data.active_count || 0;
  const atLimit = activeCount >= MAX_CASES;
  const vault = data.claimable_vault || 0;

  // Aktif kasalar önce, sonra pasifler
  const sortedCases = [...(data.cases || [])].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0));

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-24 pb-12">
      <div className={`max-w-[1800px] mx-auto px-4 flex gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
        <main className="flex-1 min-w-0">
          {/* HEADER */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className="font-heading font-black text-2xl md:text-3xl text-white uppercase tracking-wider flex items-center gap-2">
                <Boxes className="w-7 h-7 text-[#B059E8]" /> My Cases
              </h1>
              <div className="flex items-center gap-2 text-sm font-bold">
                <span className="text-zinc-200">{activeCount}<span className="text-zinc-500">/{MAX_CASES}</span></span>
                <span className="text-zinc-500 font-medium">active cases</span>
              </div>
            </div>

            <button
              onClick={() => atLimit ? toast.error(`You can have max ${MAX_CASES} active cases. Disable one first.`) : setShowCreate(true)}
              className={`px-6 py-3 rounded-xl font-black uppercase text-sm tracking-widest flex items-center justify-center gap-2 transition-transform hover:scale-105 ${
                atLimit
                  ? 'bg-[#2A2D31]/60 text-zinc-500 cursor-not-allowed hover:scale-100'
                  : 'bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] text-white shadow-[0_0_20px_rgba(176,89,232,0.35)]'
              }`}
            >
              <Plus className="w-5 h-5" /> Create Case
            </button>
          </div>

          {/* KAZANÇ / CLAIM PANELİ */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {/* Claim edilebilir */}
            <div className="sm:col-span-2 bg-gradient-to-br from-[#1A1D20] to-[#161A1D] border border-[#2A2D31] rounded-2xl p-5 flex items-center justify-between gap-4 relative overflow-hidden">
              <div className="absolute -right-6 -bottom-6 opacity-[0.06] pointer-events-none">
                <Wallet className="w-40 h-40" />
              </div>
              <div className="relative z-10">
                <p className="text-[11px] text-zinc-500 font-bold uppercase tracking-widest mb-1">Claimable Commission</p>
                <div className="flex items-end gap-1.5">
                  <span className="text-3xl md:text-4xl font-black text-[#2ecf6e] mm-num">{formatCompact(vault)}</span>
                  <span className="text-xl font-bold text-[#2ecf6e]/70 mb-1">$</span>
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">Earned from all your cases. Claim moves it to your balance.</p>
              </div>
              <button
                onClick={handleClaim}
                disabled={vault <= 0 || claiming}
                className={`relative z-10 px-6 py-3.5 rounded-xl font-black uppercase text-sm tracking-widest flex items-center gap-2 transition-transform shrink-0 ${
                  vault > 0 && !claiming
                    ? 'bg-gradient-to-r from-[#16A34A] to-[#15913f] hover:from-[#1bbe57] hover:to-[#16A34A] text-white shadow-[0_0_18px_rgba(22,163,74,0.45)] hover:scale-105'
                    : 'bg-[#2A2D31]/60 text-zinc-500 cursor-not-allowed'
                }`}
              >
                {claiming ? <Activity className="w-5 h-5 animate-spin" /> : <Coins className="w-5 h-5" />}
                Claim
              </button>
            </div>

            {/* Lifetime kazanç */}
            <div className="bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-5 flex flex-col justify-center">
              <p className="text-[11px] text-zinc-500 font-bold uppercase tracking-widest mb-1">Total Earned (Lifetime)</p>
              <div className="flex items-end gap-1.5">
                <span className="text-2xl md:text-3xl font-black text-white mm-num">{formatCompact(data.total_earnings || 0)}</span>
                <span className="text-lg font-bold text-zinc-500 mb-0.5">$</span>
              </div>
            </div>
          </div>

          {/* KASA LİSTESİ */}
          {loading ? (
            <div className="py-16 flex justify-center"><Activity className="w-8 h-8 text-[#B059E8] animate-spin" /></div>
          ) : !isAuthenticated ? (
            <div className="bg-[#1A1D20]/50 border border-[#2A2D31]/50 rounded-2xl py-16 flex flex-col items-center text-zinc-500">
              <Boxes className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm font-bold uppercase tracking-widest">Sign in to manage your cases</p>
            </div>
          ) : sortedCases.length === 0 ? (
            <div className="bg-[#1A1D20]/50 border border-[#2A2D31]/50 rounded-2xl py-16 flex flex-col items-center text-zinc-500">
              <Boxes className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm font-bold uppercase tracking-widest">No cases yet</p>
              <p className="text-xs mt-1">Create your first community case and start earning commission!</p>
              <button
                onClick={() => setShowCreate(true)}
                className="mt-5 px-6 py-3 bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] text-white rounded-xl font-black uppercase text-sm tracking-widest flex items-center gap-2 transition-transform hover:scale-105"
              >
                <Plus className="w-5 h-5" /> Create Case
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence initial={false}>
                {sortedCases.map(c => (
                  <MyCaseCard key={c.id} caseObj={c} onDelete={handleDelete} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </main>
      </div>

      {/* CREATE MODAL */}
      <AnimatePresence>
        {showCreate && (
          <CreateCaseModal
            token={token}
            onClose={() => setShowCreate(false)}
            onCreated={fetchMine}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunityCases;